import { getValidatedLimitParam, getValidatedPageParam, useCustomToast } from '@elkaso-app/web-design';
import http from 'apis/config/http-service';
import { useQueryWrapper } from 'apis/config/use-api-wrapper';
import type { AxiosError } from 'axios';
import { useIsLoggedIn } from 'hooks/use-is-logged-in';
import { TParam, usePageParams } from 'hooks/use-page-params';
import { UseQueryOptions, UseQueryResult } from 'react-query';

const endpoint = 'ums/v1/crm/locations/google/mappings';
export const getCrmGoogleLocationMappingsEndpointIdentifier = endpoint;

export type ICrmGoogleLocationMappingsData = {
  googleLocation: {
    id: number;
    name: string;
    address: string;
    type: string;
    mappingConfidence: number;
    businessStatus: string;
  };
  brand: {
    id: number;
    name: string;
  };
  location: {
    id: number;
    name: string;
    address: string;
    area: string;
    geoLocation: {
      id: number;
      name: string;
    };
  };
};

type TData = {
  pageCount: number;
  total: number;
  data: ICrmGoogleLocationMappingsData[];
};

type TError = AxiosError;
type TQueryKey = [
  typeof endpoint,
  {
    page: TParam;
    limit: TParam;
    sort: TParam;
    businessStatus: TParam;
    brandId: TParam;
    name: TParam;
  }
];

type Options = Omit<UseQueryOptions<unknown, TError, TData, TQueryKey>, 'queryKey' | 'queryFn'>;
type ReturnType = UseQueryResult<TData, TError>;

export function useGetCrmGoogleLocationMappingsApi(options?: Options): ReturnType {
  const toast = useCustomToast();
  const page = getValidatedPageParam();
  const limit = getValidatedLimitParam();
  const isLoggedIn = useIsLoggedIn();
  const { getPageParams } = usePageParams();
  const { sort, businessStatus, brandId, name } = getPageParams();
  const { ...otherOptions } = options || {};

  const queryFn = async () => {
    try {
      const params: any = {
        page,
        limit,
        sort,
      };

      if (businessStatus) {
        params.businessStatus = businessStatus;
      }

      if (name) {
        params.name = name;
      }

      if (brandId) {
        params.brandId = brandId;
      }

      const { data } = await http.get(endpoint, {
        params,
      });

      return data;
    } catch (error: any) {
      if (!error.response.status || !error.response.data.message) throw error;

      toast({
        description: error.response.data.message,
        status: 'error',
      });

      throw error;
    }
  };

  return useQueryWrapper(
    [
      endpoint,
      {
        page,
        limit,
        sort,
        businessStatus,
        brandId,
        name,
      },
    ],
    queryFn,
    {
      enabled: isLoggedIn,
      ...otherOptions,
    }
  );
}
